import { useEffect, useState } from "react";
import Shell from "../components/Shell";
import { fetchConnections, testTelegram, testEmail, fetchSettings } from "../lib/api";
import {
  PushPinIcon,
  EnvelopeIcon,
  BellRingingIcon,
  RobotIcon,
  PlugsIcon,
  ChatTeardropTextIcon,
  ArrowsClockwiseIcon,
} from "@phosphor-icons/react";
import { SecondaryButton } from "../components/ui-bits";
import { toast } from "sonner";

function StatusPill({ ok, label }) {
  return (
    <span
      className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold ${
        ok ? "bg-emerald-600 text-white" : "bg-zinc-300 text-zinc-700"
      }`}
    >
      {ok ? "✓ " : "✗ "}{label || (ok ? "live" : "down")}
    </span>
  );
}

function ConnectionCard({ icon: Icon, title, status, children, action }) {
  return (
    <div className="border border-zinc-200 bg-white p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-zinc-100 flex items-center justify-center">
            <Icon size={20} weight="bold" />
          </div>
          <div>
            <div className="font-heading font-bold text-base">{title}</div>
            <div className="mt-1">{status}</div>
          </div>
        </div>
        {action}
      </div>
      <dl className="text-xs space-y-1.5 text-zinc-700">{children}</dl>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="grid grid-cols-2 gap-2 border-t border-zinc-100 pt-1.5">
      <dt className="text-zinc-500">{label}</dt>
      <dd className={mono ? "font-mono-num text-zinc-900 truncate" : "text-zinc-900 truncate"}>{value ?? "—"}</dd>
    </div>
  );
}

function fmtTime(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

export default function ConnectionsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [settings, setSettings] = useState(null);

  const load = async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const [c, s] = await Promise.all([fetchConnections(), fetchSettings()]);
      setData(c);
      setSettings(s);
      if (manual) toast.success("Status updated");
    } catch (e) {
      if (manual) toast.error("Couldn't refresh status");
    } finally {
      setLoading(false);
      if (manual) setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  if (loading || !data) {
    return (
      <Shell title="Connections">
        <div className="text-sm text-zinc-400">Loading…</div>
      </Shell>
    );
  }

  return (
    <Shell
      title="Connections"
      subtitle="Live status of every external integration this app talks to."
      actions={
        <SecondaryButton onClick={() => load(true)} disabled={refreshing} data-testid="conn-refresh" className="flex items-center gap-2">
          <ArrowsClockwiseIcon size={14} weight="bold" className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Refreshing…" : "Refresh"}
        </SecondaryButton>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pushover */}
        <ConnectionCard
          icon={PushPinIcon}
          title="Pushover"
          status={<StatusPill ok={data.pushover.configured} />}
          action={
            <SecondaryButton
              data-testid="conn-pushover-test"
              onClick={async () => {
                const r = await testTelegram();
                if (r.sent) toast.success("Pushover test sent");
                else toast.error(r.error || "Failed");
              }}
              className="!px-3 !py-1.5 text-xs"
            >
              Test
            </SecondaryButton>
          }
        >
          <Row label="User key set" value={data.pushover.user_key_set ? "yes" : "no"} />
          <Row label="Last successful alert" value={fmtTime(data.pushover.last_sent_at)} mono />
        </ConnectionCard>

        {/* M365 Email */}
        <ConnectionCard
          icon={EnvelopeIcon}
          title="Email · Microsoft 365 (Entra)"
          status={<StatusPill ok={data.email_m365.configured} />}
          action={
            <SecondaryButton
              data-testid="conn-email-test"
              onClick={async () => {
                const r = await testEmail(data.email_m365.destination || data.email_m365.mailbox);
                if (r.sent) toast.success(`Email sent to ${r.to}`);
                else toast.error(r.error || "Failed");
              }}
              disabled={!data.email_m365.configured}
              className="!px-3 !py-1.5 text-xs"
            >
              Test
            </SecondaryButton>
          }
        >
          <Row label="Tenant" value={data.email_m365.tenant_id} mono />
          <Row label="Sender mailbox" value={data.email_m365.mailbox} mono />
          <Row label="Destination" value={data.email_m365.destination} mono />
          <Row label="Alert on HIGH" value={data.email_m365.alert_on_high ? "on" : "off"} />
          <Row label="Digest" value={data.email_m365.digest_enabled ? "enabled" : "off"} />
          <Row label="Last digest sent" value={fmtTime(data.email_m365.last_digest_sent_at)} mono />
        </ConnectionCard>

        {/* Web Push */}
        <ConnectionCard
          icon={BellRingingIcon}
          title="Web Push (PWA)"
          status={<StatusPill ok={data.web_push.configured} />}
        >
          <Row label="VAPID configured" value={data.web_push.configured ? "yes" : "no"} />
          <Row label="Subscribers" value={data.web_push.subscribers} mono />
        </ConnectionCard>

        {/* LLM */}
        <ConnectionCard
          icon={RobotIcon}
          title="LLM · GPT-4.1-mini"
          status={<StatusPill ok={data.llm.configured && data.llm.enabled} label={data.llm.enabled ? "on" : "paused"} />}
        >
          <Row label="Model" value={data.llm.model} mono />
          <Row label="Enabled" value={data.llm.enabled ? "yes" : "no (off by default)"} />
          <Row label="Calls (30d)" value={data.llm.calls_30d} mono />
          <Row label="Cost (30d)" value={`€${data.llm.cost_eur_30d}`} mono />
          <Row label="Budget" value={`€${data.llm.budget_eur} (${data.llm.budget_used_pct}% used)`} mono />
        </ConnectionCard>

        {/* WhatsApp Listener */}
        <ConnectionCard
          icon={ChatTeardropTextIcon}
          title="WhatsApp Listener (mini-PC)"
          status={
            <StatusPill
              ok={data.listener.alive}
              label={data.listener.alive ? "live" : data.listener.last_ingest_at ? "stale" : "never seen"}
            />
          }
        >
          <Row label="Last ingest" value={fmtTime(data.listener.last_ingest_at)} mono />
          <Row
            label="Last from"
            value={data.listener.last_group || "—"}
          />
          <Row
            label="Minutes since"
            value={data.listener.minutes_since_last != null ? data.listener.minutes_since_last : "—"}
            mono
          />
          {!data.listener.alive && data.listener.last_ingest_at && (
            <div className="mt-3 border border-amber-300 bg-amber-50 p-3 text-[11px] leading-relaxed text-amber-900" data-testid="listener-stale-hint">
              <strong>Listener is STALE.</strong> The Docker container can be running and still be stale —
              that only means no message has been forwarded recently. Most common cause: the WhatsApp
              session inside the container got <strong>logged out / disconnected</strong>.
              <div className="mt-1.5">Fix on your mini-PC:</div>
              <ol className="list-decimal ml-4 mt-1 space-y-0.5">
                <li>Open the container <strong>Logs</strong> in Docker Desktop.</li>
                <li>If you see a <strong>QR code</strong> or "Disconnected" → open WhatsApp → Linked Devices → re-scan the QR.</li>
                <li>If it never prints "Ready" → restart the container.</li>
              </ol>
            </div>
          )}
        </ConnectionCard>

        {/* Twilio (placeholder) */}
        <ConnectionCard
          icon={PlugsIcon}
          title="Twilio SMS (optional)"
          status={<StatusPill ok={false} label="not enabled" />}
        >
          <Row label="Status" value="placeholder — not wired" />
          <Row label="Use case" value="SMS fallback alerts (4th channel)" />
        </ConnectionCard>
      </div>

      <div className="border border-zinc-200 bg-zinc-50 p-6 mt-8 text-xs text-zinc-600 leading-relaxed">
        <strong>How to interpret:</strong> The dashboard polls every 10 s. A channel is <em>configured</em>{" "}
        when credentials are present in <code className="font-mono-num">backend/.env</code>; it doesn't guarantee
        delivery (use <em>Test</em>). The WhatsApp listener is marked <em>live</em> if it forwarded any message in
        the last 10 minutes — that's the heartbeat.
      </div>
    </Shell>
  );
}
